/**
 * Permissões de uma pessoa.
 *
 * A tela **não tem lógica de permissão própria**. Ela chama as mesmas funções
 * de `@nv/core` que o servidor chama — `effectivePermissions` para mostrar o
 * resultado e `checkPermissionEdit` para saber o que desabilitar. Duas
 * implementações da mesma regra divergiriam, e a divergência apareceria como
 * "salvei e não salvou".
 *
 * O que a tela decide é ergonomia: o que aparece, o que fica em cinza, o que
 * some. **Quem autoriza é o servidor**, e cada uma das recusas daqui tem um
 * teste do outro lado provando que o endpoint recusa igual.
 *
 * Edição offline é bloqueada por desenho: a decisão depende de estado que só o
 * servidor conhece (o papel de quem edita, o que ele mesmo tem, o estado atual
 * do alvo). Enfileirar produziria uma promessa que talvez fosse recusada — e
 * permissão prometida e não concedida é pior do que permissão negada na hora.
 */
import {
  ASSIGNABLE_ROLES,
  canManagePermissions,
  checkPermissionEdit,
  effectivePermissions,
  minimalOverrides,
  PERMISSION_EDIT_MESSAGES,
  type PermissionKey,
  ROLE_LABELS,
  ROLE_PRESETS,
  type TripRole,
  VISIBLE_PERMISSION_KEYS,
} from '@nv/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useOnline } from '../../sync/useOnline';
import { Card, ErrorState, SectionLabel, Skeleton } from '../../ui/primitives';
import { GrupoError, readPermissions, removeMember, savePermissions } from './api';
import { ROTULOS_PERMISSAO as ROTULOS } from './permissaoLabels';


export function PermissoesScreen({
  tripId,
  ator,
}: {
  tripId: string;
  ator: { memberId: string; role: TripRole; permissions: string[] };
}) {
  const { membro: memberId } = useParams<{ membro: string }>();
  const navegar = useNavigate();
  const cliente = useQueryClient();
  const online = useOnline();

  const [papel, setPapel] = useState<TripRole>('participant');
  const [marcadas, setMarcadas] = useState<Set<PermissionKey>>(new Set());
  const [confirmandoRemocao, setConfirmandoRemocao] = useState(false);

  const dados = useQuery({
    queryKey: ['permissoes', tripId, memberId],
    queryFn: () => readPermissions(tripId, memberId ?? ''),
    enabled: Boolean(memberId),
  });

  // Espelha o servidor assim que ele responde. O estado local é rascunho de
  // edição, não fonte da verdade.
  useEffect(() => {
    if (!dados.data) return;
    setPapel(dados.data.member.role);
    setMarcadas(new Set(dados.data.member.effective as PermissionKey[]));
  }, [dados.data]);

  const salvar = useMutation({
    mutationFn: () =>
      savePermissions(tripId, memberId ?? '', { role: papel, permissions: [...marcadas] }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['permissoes', tripId, memberId] });
      // As permissões efetivas da sessão podem ter mudado — inclusive as de
      // quem está olhando, se ele editou a si mesmo por outro caminho.
      void cliente.invalidateQueries({ queryKey: ['viagem'] });
    },
  });

  const remover = useMutation({
    mutationFn: () => removeMember(tripId, memberId ?? ''),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['viagem'] });
      void cliente.invalidateQueries({ queryKey: ['grupo', tripId] });
      navegar('/grupo', { replace: true });
    },
  });

  if (dados.isLoading) {
    return (
      <section className="grupo">
        <Skeleton height={44} count={5} />
      </section>
    );
  }

  if (dados.isError) {
    const erro = dados.error as GrupoError;

    // Offline tem estado próprio, e não o genérico de falha: a explicação é
    // outra, e a ação também. "Tente de novo" sem rede é um botão que mente.
    if (!online) {
      return (
        <section className="grupo">
          <header className="fin-nova__header">
            <h1 className="grupo__titulo">Permissões</h1>
          </header>
          <Card variant="chip">
            <p className="auth-note" role="status">
              Sem conexão. Permissões não podem ser vistas nem alteradas
              offline — quem decide o que você pode conceder é o servidor, com o
              estado atual da viagem.
            </p>
          </Card>
          <button
            type="button"
            className="nv-btn nv-btn--secondary nv-btn--block"
            disabled
          >
            Salvar permissões
          </button>
        </section>
      );
    }

    return (
      <section className="grupo">
        <ErrorState
          title={
            erro.code === 'not_found' ? 'Participante não encontrado' : 'Não foi possível carregar'
          }
          description={
            erro.code === 'not_found'
              ? 'A pessoa pode ter saído da viagem.'
              : 'Verifique a conexão e tente de novo.'
          }
          onRetry={() => void dados.refetch()}
        />
      </section>
    );
  }

  const alvo = dados.data!.member;
  const historico = dados.data!.history;

  const efetivas = effectivePermissions(papel, minimalOverrides(papel, marcadas));
  const permissoesDoAtor = new Set(ator.permissions as PermissionKey[]);

  /**
   * Sem acesso: ver o papel de quem viaja com você não é privilégio (a rota de
   * leitura é `trip:read`), mas editar exige `manage_permissions`.
   *
   * Quem não tem a chave recebe a mesma informação em modo leitura, e não um
   * formulário inteiro que termina em 403 ao salvar.
   */
  const podeGerenciar = canManagePermissions({ role: ator.role, permissions: permissoesDoAtor });

  if (!podeGerenciar) {
    return (
      <section className="grupo">
        <header className="fin-nova__header">
          <div>
            <h1 className="grupo__titulo">{alvo.name}</h1>
            <p className="grupo__contagem">{ROLE_LABELS[alvo.role]} nesta viagem</p>
          </div>
        </header>

        <Card variant="chip">
          <p className="auth-note" role="status">
            {PERMISSION_EDIT_MESSAGES.missing_manage_permissions} Você está vendo
            o que esta pessoa pode fazer, sem poder alterar.
          </p>
        </Card>

        <SectionLabel>PERMISSÕES</SectionLabel>
        <Card variant="surf">
          {VISIBLE_PERMISSION_KEYS.filter((k) => alvo.effective.includes(k)).map((k) => (
            <div className="perm__linha perm__linha--bloqueada" key={k}>
              <span className="perm__texto">
                <span className="perm__rotulo">{ROTULOS[k]}</span>
              </span>
            </div>
          ))}
        </Card>
      </section>
    );
  }

  const veredito = checkPermissionEdit({
    actor: { memberId: ator.memberId, role: ator.role, permissions: permissoesDoAtor },
    target: { memberId: alvo.memberId, role: alvo.role, overrides: alvo.overrides as never },
    edit: { role: papel, overrides: minimalOverrides(papel, marcadas) },
  });

  /** Uma chave é editável se o ator é dono ou se ele mesmo a tem. */
  const podeMexerEm = (k: PermissionKey) =>
    ator.role === 'owner' || permissoesDoAtor.has(k);

  const bloqueadoPorInvariante =
    alvo.memberId === ator.memberId || alvo.role === 'owner';

  const podeRemover =
    permissoesDoAtor.has('manage_members') &&
    alvo.role !== 'owner' &&
    alvo.memberId !== ator.memberId;

  const semMudanca =
    veredito.ok && veredito.changed.length === 0 && papel === alvo.role;

  return (
    <section className="grupo">
      <header className="fin-nova__header">
        <div>
          <h1 className="grupo__titulo">{alvo.name}</h1>
          <p className="grupo__contagem">{ROLE_LABELS[alvo.role]} nesta viagem</p>
        </div>
      </header>

      {!online && (
        <Card variant="chip">
          <p className="auth-note" role="status">
            Sem conexão. Permissões só podem ser alteradas online — a decisão
            depende do estado atual no servidor.
          </p>
        </Card>
      )}

      {bloqueadoPorInvariante && (
        <Card variant="chip">
          <p className="auth-note" role="status">
            {alvo.memberId === ator.memberId
              ? PERMISSION_EDIT_MESSAGES.cannot_edit_self
              : PERMISSION_EDIT_MESSAGES.cannot_edit_owner}
          </p>
        </Card>
      )}

      <SectionLabel>PAPEL</SectionLabel>
      <div className="fin-nova__chips">
        {ASSIGNABLE_ROLES.map((r) => (
          <button
            key={r}
            type="button"
            className={papel === r ? 'fin-chip fin-chip--ativo' : 'fin-chip'}
            disabled={bloqueadoPorInvariante || !online}
            onClick={() => {
              setPapel(r);
              // Trocar o preset redefine as marcações: o preset é o ponto de
              // partida, e manter as antigas transformaria toda troca de papel
              // num conjunto de overrides invisíveis.
              setMarcadas(new Set(ROLE_PRESETS[r]));
            }}
          >
            {ROLE_LABELS[r]}
          </button>
        ))}
      </div>

      <SectionLabel>PERMISSÕES</SectionLabel>
      <Card variant="surf">
        {VISIBLE_PERMISSION_KEYS.map((k) => {
          const doPreset = ROLE_PRESETS[papel].includes(k);
          const ligada = efetivas.has(k);
          const editavel = podeMexerEm(k) && !bloqueadoPorInvariante && online;

          return (
            <label
              className={editavel ? 'perm__linha' : 'perm__linha perm__linha--bloqueada'}
              key={k}
            >
              <input
                type="checkbox"
                checked={ligada}
                disabled={!editavel}
                onChange={() =>
                  setMarcadas((atual) => {
                    const nova = new Set(atual);
                    if (nova.has(k)) nova.delete(k);
                    else nova.add(k);
                    return nova;
                  })
                }
              />
              <span className="perm__texto">
                <span className="perm__rotulo">{ROTULOS[k]}</span>
                <span className="perm__nota">
                  {!podeMexerEm(k)
                    ? 'Você não tem esta permissão, então não pode concedê-la'
                    : ligada === doPreset
                      ? `Do preset ${ROLE_LABELS[papel]}`
                      : ligada
                        ? 'Concedida além do papel'
                        : 'Retirada do papel'}
                </span>
              </span>
            </label>
          );
        })}
      </Card>

      {!veredito.ok && (
        <p className="auth-error" role="alert">
          {PERMISSION_EDIT_MESSAGES[veredito.reason]}
        </p>
      )}

      {salvar.isError && (
        <p className="auth-error" role="alert">
          {(salvar.error as GrupoError).message}
        </p>
      )}

      <button
        type="button"
        className="nv-btn nv-btn--primary nv-btn--block"
        disabled={!veredito.ok || semMudanca || salvar.isPending || !online}
        onClick={() => salvar.mutate()}
      >
        {salvar.isPending ? 'Salvando…' : 'Salvar permissões'}
      </button>

      {podeRemover && (
        <>
          <SectionLabel>ZONA SENSÍVEL</SectionLabel>
          {confirmandoRemocao ? (
            <Card variant="surf">
              <p className="auth-note">
                {alvo.name} perde o acesso à viagem imediatamente. O que essa
                pessoa registrou continua na viagem.
              </p>
              <div className="fin__ctas">
                <button
                  type="button"
                  className="nv-btn nv-btn--secondary"
                  onClick={() => setConfirmandoRemocao(false)}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="nv-btn nv-btn--danger"
                  disabled={remover.isPending || !online}
                  onClick={() => remover.mutate()}
                >
                  Remover {alvo.name}
                </button>
              </div>
            </Card>
          ) : (
            <button
              type="button"
              className="nv-btn nv-btn--ghost-danger nv-btn--block"
              disabled={!online}
              onClick={() => setConfirmandoRemocao(true)}
            >
              Remover {alvo.name} da viagem
            </button>
          )}
          {remover.isError && (
            <p className="auth-error" role="alert">
              {(remover.error as GrupoError).message}
            </p>
          )}
        </>
      )}

      {historico.length > 0 && (
        <>
          <SectionLabel>HISTÓRICO</SectionLabel>
          <Card variant="surf">
            {historico
              .slice()
              .reverse()
              .map((h) => (
                <div className="perm__historico" key={h.id}>
                  <span className="perm__historico-quem">{h.actorName}</span>
                  <span className="perm__historico-o-que">
                    {h.action === 'member.remove'
                      ? 'removeu da viagem'
                      : (h.after?.changed?.length ?? 0) > 0
                        ? `alterou ${h.after?.changed?.map((k) => ROTULOS[k as PermissionKey] ?? k).join(', ')}`
                        : `definiu o papel como ${ROLE_LABELS[(h.after?.role ?? 'participant') as TripRole]}`}
                  </span>
                  <span className="perm__historico-quando">
                    {new Date(h.createdAt).toLocaleString('pt-BR', {
                      day: '2-digit',
                      month: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                </div>
              ))}
          </Card>
        </>
      )}

      <Card variant="chip">
        <p className="auth-note">
          Apenas um dono por viagem. A tela reflete o que o servidor decide — se
          algo aqui parecer permitido e for recusado, quem manda é a resposta do
          servidor.
        </p>
      </Card>
    </section>
  );
}
